import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppAutocomplete } from "./form";
import { useDeploymentMode } from "../hooks/useDeploymentMode";
import { adminVpnProfileService, type VpnProfileSummary } from "../services/adminVpnProfileService";

type Option = { value: string; label: string };

type Props = {
  /** Selected VPN profile id; "" means no VPN. */
  value: string;
  onChange: (value: string) => void;
  /** Name of the currently assigned profile, shown even if it is disabled (not in the summaries list). */
  currentName?: string | null;
  disabled?: boolean;
};

/** VPN profile picker for data adaptors and OpenAPI catalogs. Renders nothing when VPN is off on the deployment. */
export default function VpnProfileSelect({ value, onChange, currentName, disabled }: Props) {
  const { t } = useTranslation();
  const { vpnEnabled } = useDeploymentMode();
  const [profiles, setProfiles] = useState<VpnProfileSummary[]>([]);

  useEffect(() => {
    if (!vpnEnabled) return;
    let cancelled = false;
    adminVpnProfileService
      .summaries()
      .then((list) => {
        if (!cancelled) setProfiles(list);
      })
      .catch(() => {
        if (!cancelled) setProfiles([]);
      });
    return () => {
      cancelled = true;
    };
  }, [vpnEnabled]);

  const options = useMemo<Option[]>(() => {
    const list: Option[] = [{ value: "", label: t("vpnProfiles.none") }];
    profiles.forEach((p) => list.push({ value: p.id, label: p.name }));
    if (value && !profiles.some((p) => p.id === value)) {
      list.push({ value, label: currentName || value });
    }
    return list;
  }, [profiles, value, currentName, t]);

  if (!vpnEnabled) return null;

  return (
    <AppAutocomplete<Option>
      label={t("vpnProfiles.assignLabel")}
      options={options}
      value={options.find((o) => o.value === value) ?? options[0]}
      disableClearable
      autoHighlight
      openOnFocus
      disabled={disabled}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      onChange={(_, option) => onChange(option?.value ?? "")}
      helperText={t("vpnProfiles.assignHelp")}
    />
  );
}
